import React, { FC, useState } from 'react';
import { Proposal, TransferProposal } from 'ts/shared/interfaces/CreateProposalParams';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChild,
  faLocationDot,
  faPencil,
  faPenToSquare,
  faPerson,
  faPlane,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import { getCurrency } from 'utils/utils';
import { formatPriceCurrency } from 'utils/utils';
import FreeTextModal from '../Single/FreeTextModal';
import { formatPriceProposal, dateFormat } from 'utils/shared/utils';
import { useProposalUi } from '../Assistant/proposalActions';

interface TrnProposalProps {
  transfer: TransferProposal;
  proposal: Proposal;
  setProposal: (proposal: Proposal) => void;
  proposalIndex: number;
  openPopup: (product: string, index: number) => void;
  assistantOwner?: boolean;
}
const TrnProposal: FC<TrnProposalProps> = ({ transfer, assistantOwner = false, ...props }) => {
  const { t } = useTranslation();
  const [showFreeModal, setShowFreeModal] = useState(false);

  useProposalUi(
    (intent) =>
      !!assistantOwner &&
      intent.kind === 'open-edit' &&
      intent.product === 'transfers' &&
      !intent.dynamic &&
      intent.index === props.proposalIndex,
    (intent) => {
      if (intent.kind === 'open-edit') {
        setShowFreeModal(true);
      }
    },
  );

  const forwardItem = transfer.forwardItem?.transfer;
  const returnItem = transfer.returnItem?.transfer;

  const onSaveFreeText = (desc: string, displayOrder?: number) => {
    props.setProposal({
      ...props.proposal,
      transfers: props.proposal.transfers.map((f, i) =>
        i === props.proposalIndex ? { ...f, freeText: desc, displayOrder } : f
      ),
    });
  };

  const getPlaneOrLocation = (transfer?: any) => {
    if (transfer?.airport) {
      return (
        <span>
          <FontAwesomeIcon icon={faPlane} />
          <span className="ml-5">{transfer.airport.desc}</span>
        </span>
      );
    } else if (transfer?.city) {
      return (
        <span>
          <FontAwesomeIcon icon={faLocationDot} />
          <span className="ml-5">{transfer.city.desc}</span>
        </span>
      );
    }
  };

  return (
    <tr>
      <td width={60}>#{props.proposalIndex + 1}</td>
      <td>
        {!!forwardItem?.adult && (
          <span>
            {forwardItem?.adult} *
            <FontAwesomeIcon icon={faPerson} className="mr-5 ml-5" />
            {t('search.adults')}
          </span>
        )}
        {!!forwardItem?.cnn && (
          <span>
            , {forwardItem?.cnn} *
            <FontAwesomeIcon icon={faChild} className="mr-5 ml-5" />
            {t('search.children')}
          </span>
        )}
      </td>
      <td>
        {dateFormat(forwardItem?.pickUpDate, 'dd MMM, yyyy HH:mm')}
        {returnItem && (
          <div>
            <div className="divider mb-15 mt-15"></div>
            {dateFormat(returnItem?.pickUpDate, 'dd MMM, yyyy HH:mm')}
          </div>
        )}
      </td>
      <td className="text-lh">
        <div>{getPlaneOrLocation(forwardItem?.pickUpDetails)}</div>
        <div>{getPlaneOrLocation(forwardItem?.dropOffDetails)}</div>

        {returnItem && (
          <div>
            <div className="divider mb-5 mt-5"></div>
            <div>{getPlaneOrLocation(returnItem.pickUpDetails)}</div>
            <div>{getPlaneOrLocation(returnItem.dropOffDetails)}</div>
          </div>
        )}
      </td>
      <td>
        {forwardItem?.transferVehicles[0].vehicle.desc}
        {returnItem && (
          <div>
            <div className="divider mb-15 mt-15"></div>
            {returnItem?.transferVehicles[0].vehicle.desc}
          </div>
        )}
      </td>
      <td className="text-end" width={130}>
        {!!transfer.viewValue && (
          <span className="text-contrast">
            {formatPriceCurrency(transfer.price, getCurrency(transfer.currency))} /{' '}
          </span>
        )}
        <span
          className="text-bold text-lg text-contrast text-nowrap cur-poi"
          onClick={() => props.openPopup('transfers', props.proposalIndex)}
        >
          {formatPriceProposal(
            transfer.price,
            transfer.currency,
            transfer.viewType,
            transfer.viewValue
          )}
          <FontAwesomeIcon icon={faPencil} className="cur-poi ml-5" size="xs" />
        </span>
      </td>
      <td>
        <div className="flex-row-end mr-10">
          <FontAwesomeIcon
            icon={faPenToSquare}
            className="cur-poi ml-5"
            size="lg"
            onClick={() => setShowFreeModal(true)}
          />
          <FontAwesomeIcon
            icon={faTrash}
            size="lg"
            className="ml-10 cur-poi"
            onClick={() => {
              props.setProposal({
                ...props.proposal,
                transfers: props.proposal.transfers.filter((f, i) => i !== props.proposalIndex),
              });
            }}
          />
        </div>
      </td>
      {showFreeModal && (
        <FreeTextModal
          close={() => setShowFreeModal(false)}
          onSave={onSaveFreeText}
          freeText={transfer.freeText || ''}
          displayOrder={transfer.displayOrder}
        />
      )}
    </tr>
  );
};

export default TrnProposal;
